import {
  VOUCHERIFY_API_KEY,
  VOUCHERIFY_APP_TOKEN,
  VOUCHERIFY_DOMAIN
} from 'config/server'
import createCache from '@jarvis.oddle/core/server/cache/cache'
import { isEmpty } from '@jarvis.oddle/core/tools/string'
import { isObjectEmpty } from '@jarvis.oddle/core/tools/object'
import logger from '@jarvis.oddle/core/tools/logger'
import _request from '@jarvis.oddle/core/tools/request'

export const getListOfVouchersPath = '/vouchers/qualifications'
export const validateVoucherPath = code => `/vouchers/${code}/validate`
export const deleteVoucherSessionPath = (code, sessionKey) => `/vouchers/${code}/sessions/${sessionKey}`

export const adjustDecimal = (orderMetadata) => {
  // price must be multiplied by 100 to precisely represents 2 decimal places
  // https://docs.voucherify.io/reference#validate-voucher
  try {
    return {
      ...orderMetadata,
      amount: orderMetadata.amount * 100,
      items: (orderMetadata.items || []).map((item) => ({
        ...item,
        price: item.price * 100
      }))
    }
  } catch (_) {
    return orderMetadata
  }
}

export const normalizeDecimal = (originalVoucher) => {
  try {
    const normalizedVoucher = { ...originalVoucher }

    // The returned value from voucherify api is multiplied by 100 to precisely represent 2 decimal places.
    // For example, $100 is written as 10000.
    // Therefore, we divide the values by 100 to display the true original amount
    // https://docs.voucherify.io/reference#the-order-object
    if (!isObjectEmpty(originalVoucher.order)) {
      normalizedVoucher.order = {
        ...(normalizedVoucher.order),
        amount: normalizedVoucher.order.amount / 100,
        discount_amount: normalizedVoucher.order.discount_amount / 100,
        total_amount: normalizedVoucher.order.total_amount / 100,
        total_discount_amount: normalizedVoucher.order.total_discount_amount / 100,
        items: (normalizedVoucher.order.items || []).map((item) => ({
          ...item,
          price: item.price / 100,
          amount: item.amount / 100
        }))
      }
    }

    if (!isObjectEmpty(originalVoucher.discount) && !isNaN(originalVoucher.discount.amount_off)) {
      normalizedVoucher.discount = {
        ...(normalizedVoucher.discount),
        amount_off: normalizedVoucher.discount.amount_off / 100
      }
    }

    return normalizedVoucher
  } catch (_) {
    return originalVoucher
  }
}

const request = async (options) => {
  logger.verbose(`[voucherify] ${options.uri} _requested ${JSON.stringify(options, null, 2)}`)
  const response = await _request(options)
  logger.verbose(`[voucherify] ${options.uri} got response ${response.body} ${response.statusCode}`)
  return response
}

export function createApi (express, apiRoot, cacheStorage) {
  const router = new express.Router()

  const voucherSessionCache = createCache({
    name: 'VoucherSessionCache',
    storage: cacheStorage,
    getCacheKey: (sessionKey) => sessionKey,
    shouldReadFromCache: () => true,
    shouldSaveToCache: () => true
  })

  router.post(getListOfVouchersPath, async (req, res, next) => {
    try {
      const { customer, order } = req.body
      const [
        personalVouchersResponse,
        publicVouchersResponse,
        qualifiedVouchersResponse,
        qualifiedCampaignsResponse
      ] = await Promise.all([
        request({
          uri: `${VOUCHERIFY_DOMAIN}/v1/vouchers?customer=${req.body.customer.source_id}&limit=100`,
          method: 'GET',
          headers: {
            'X-App-Id': VOUCHERIFY_API_KEY,
            'X-App-Token': VOUCHERIFY_APP_TOKEN,
            'Content-Type': 'application/json'
          }
        }),
        request({
          uri: `${VOUCHERIFY_DOMAIN}/v1/vouchers?category=public&limit=100`,
          method: 'GET',
          headers: {
            'X-App-Id': VOUCHERIFY_API_KEY,
            'X-App-Token': VOUCHERIFY_APP_TOKEN,
            'Content-Type': 'application/json'
          }
        }),
        request({
          uri: `${VOUCHERIFY_DOMAIN}/v1/vouchers/qualification`,
          method: 'POST',
          headers: {
            'X-App-Id': VOUCHERIFY_API_KEY,
            'X-App-Token': VOUCHERIFY_APP_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customer,
            order: adjustDecimal(order)
          })
        }),
        request({
          uri: `${VOUCHERIFY_DOMAIN}/v1/campaigns/qualification?limit=100`,
          method: 'POST',
          headers: {
            'X-App-Id': VOUCHERIFY_API_KEY,
            'X-App-Token': VOUCHERIFY_APP_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customer,
            order: adjustDecimal(order)
          })
        })
      ])

      const parsedQualifiedVouchers = JSON.parse(qualifiedVouchersResponse.body)
      const parsedQualifiedCampaigns = JSON.parse(qualifiedCampaignsResponse.body)
      const parsedPersonalVouchers = JSON.parse(personalVouchersResponse.body)
      const parsedPublicVouchers = JSON.parse(publicVouchersResponse.body)

      const qualifiedVouchers = parsedQualifiedVouchers.data.reduce((obj, voucher) => {
        obj[voucher.id] = true
        return obj
      }, {})
      const qualifiedCampaigns = parsedQualifiedCampaigns.data.reduce((obj, campaign) => {
        obj[campaign.id] = true
        return obj
      }, {})

      const listOfVouchers = [...parsedPersonalVouchers.vouchers, ...parsedPublicVouchers.vouchers]

      const enhancedListOfVouchers = listOfVouchers
        .filter(voucher => {
          if (!voucher.active) {
            return false
          }
          // only remove fully redeemed voucher if showEvenWhenFullyRedeemed is not true
          if (
            (voucher.redemption.quantity === voucher.redemption.redeemed_quantity) &&
            !voucher.metadata.showEvenWhenFullyRedeemed
          ) {
            return false
          }
          if (voucher.expiration_date) {
            return new Date(voucher.expiration_date).getTime() >= new Date().getTime()
          }
          return true
        })
        .map(voucher => {
          const normalizedVoucher = normalizeDecimal(voucher)
          normalizedVoucher.isValid =
           qualifiedVouchers[voucher.id] ||
           qualifiedCampaigns[voucher.campaign_id] ||
          voucher.metadata.type === 'payment'
          // should disable fully redeemed voucher which have showEvenWhenFullyRedeemed's value is true
          if (
            (voucher.redemption.quantity === voucher.redemption.redeemed_quantity) &&
            voucher.metadata.showEvenWhenFullyRedeemed
          ) {
            normalizedVoucher.isValid = false
          }

          return normalizedVoucher
        })

      const validVouchers = enhancedListOfVouchers.filter(voucher => voucher.isValid)
      const invalidVouchers = enhancedListOfVouchers.filter(voucher => !voucher.isValid)
      const sortedVouchers = [...validVouchers, ...invalidVouchers]

      return res.status(200).json({
        total: parsedPersonalVouchers.total + parsedPublicVouchers.total,
        data: sortedVouchers,
        totalCampaigns: parsedQualifiedCampaigns.total
      })
    } catch (error) {
      next(new Error(error))
    }
  })

  router.post(validateVoucherPath(':code'), async (req, res, next) => {
    try {
      const { customer, order } = req.body

      const sessionKey = customer.source_id
      const { code: nextCode } = req.params
      const previousCode = await voucherSessionCache.read(sessionKey)

      if (!isEmpty(previousCode)) {
        await request({
          uri: `${VOUCHERIFY_DOMAIN}/v1${deleteVoucherSessionPath(previousCode, sessionKey)}`,
          method: 'DELETE',
          headers: {
            'X-App-Id': VOUCHERIFY_API_KEY,
            'X-App-Token': VOUCHERIFY_APP_TOKEN,
            'Content-Type': 'application/json'
          }
        })

        await voucherSessionCache.save(sessionKey, null)
      }

      const response = await request({
        uri: `${VOUCHERIFY_DOMAIN}/v1${validateVoucherPath(nextCode)}`,
        method: 'POST',
        headers: {
          'X-App-Id': VOUCHERIFY_API_KEY,
          'X-App-Token': VOUCHERIFY_APP_TOKEN,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer,
          order: adjustDecimal(order),
          session: {
            key: sessionKey,
            type: 'LOCK',
            ttl: '15',
            ttl_unit: 'MINUTES'
          }
        })
      })

      await voucherSessionCache.save(sessionKey, nextCode)
      const parsedBody = JSON.parse(response.body)

      return res.status(parsedBody.valid ? 200 : 400).json({
        data: normalizeDecimal(parsedBody)
      })
    } catch (error) {
      next(new Error(error))
    }
  })

  return [apiRoot, router]
}
